import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Minus, LayoutGrid, Rows3 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

const COMPARE_ROWS = [
  { label: "Pages", values: ["Up to 5", "Up to 8", "Bespoke"] },
  { label: "Custom domain connection", values: [true, true, true] },
  { label: "Enquiry form + email setup", values: [true, true, true] },
  { label: "Visual direction", values: ["Premium", "Bespoke", "Custom creative direction"] },
  { label: "Animations", values: ["Basic", "Advanced", "Cinematic"] },
  { label: "Conversion-focused structure", values: [false, true, true] },
  { label: "Selective custom 3D", values: [false, false, true] },
  { label: "Booking or advanced forms", values: [false, true, true] },
  { label: "Analytics integration", values: [false, true, true] },
  { label: "SEO foundations", values: ["Basic", "Enhanced", "Expanded"] },
  { label: "Support", values: ["Launch support", "Launch support", "Priority + personal launch support"] },
];

const CompareCell = ({ value }) => {
  if (value === true) return <Check size={15} className="mx-auto text-white" strokeWidth={2.5} />;
  if (value === false) return <Minus size={14} className="mx-auto text-white/20" />;
  return <span className="text-[12px] font-light text-white/70">{value}</span>;
};

const CompareTable = ({ onChoose }) => (
  <motion.div
    key="compare"
    data-testid="package-comparison-table"
    initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    exit={{ opacity: 0, y: 24, filter: "blur(8px)" }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="mt-14 overflow-x-auto rounded-3xl glass"
  >
    <table className="w-full min-w-[720px] border-collapse text-left">
      <thead>
        <tr className="border-b border-white/10">
          <th className="p-5 sm:p-6 font-display text-[10px] font-medium tracking-[0.3em] text-white/40 uppercase">Feature</th>
          {PACKAGES.map((p) => (
            <th key={p.name} className="p-5 sm:p-6 text-center">
              <span className={`font-display block text-[11px] font-semibold tracking-[0.25em] uppercase ${p.popular ? "text-white" : "text-white/60"}`}>
                {p.displayName} {p.popular && <span className="ml-1 text-white/40 normal-case tracking-normal">★</span>}
              </span>
              <span className="font-display mt-1 block text-lg font-bold chrome-text">{p.price}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {COMPARE_ROWS.map((row, ri) => (
          <tr key={row.label} className={`border-b border-white/5 ${ri % 2 ? "bg-white/[0.02]" : ""}`}>
            <td className="p-4 sm:p-5 text-[13px] font-light text-white/60">{row.label}</td>
            {row.values.map((v, vi) => (
              <td key={vi} className="p-4 sm:p-5 text-center" data-testid={`compare-cell-${ri}-${vi}`}>
                <CompareCell value={v} />
              </td>
            ))}
          </tr>
        ))}
        <tr>
          <td className="p-5" />
          {PACKAGES.map((p) => (
            <td key={p.name} className="p-4 text-center">
              <button
                data-testid={`compare-cta-${p.name.toLowerCase().replace(" ", "-")}`}
                onClick={() => { onChoose(PACKAGE_OPTIONS[p.name]); scrollToSection("contact"); }}
                className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-colors duration-300 ${
                  p.popular ? "bg-white text-black hover:bg-white/85" : "border border-white/25 text-white hover:bg-white hover:text-black"
                }`}
              >
                {p.name === "Black Label" ? "Discuss Black Label" : `Select ${p.name}`}
              </button>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </motion.div>
);

const PACKAGES = [
  {
    name: "Launch",
    displayName: "CLOSER. LAUNCH",
    price: "£399",
    tagline: "For businesses that need a professional online presence.",
    features: [
      "Up to 5 essential pages", "Premium responsive design", "Contact or enquiry form",
      "Social-media links", "Google Maps integration", "Basic animations", "Basic SEO setup",
      "Domain connection support", "Mobile, tablet and desktop optimisation", "Final quality checks and handover",
    ],
  },
  {
    name: "Growth",
    displayName: "CLOSER. GROWTH",
    price: "£699",
    tagline: "For businesses ready for a more distinctive, conversion-focused website.",
    popular: true,
    features: [
      "Everything in Launch", "Up to 8 pages", "Bespoke visual direction", "Advanced animations",
      "Conversion-focused page structure", "Reviews and FAQ sections", "Booking, quotation or advanced enquiry form",
      "WhatsApp contact option", "Enhanced gallery or portfolio", "Analytics integration", "Final quality checks and handover",
    ],
  },
  {
    name: "Black Label",
    displayName: "CLOSER. BLACK LABEL",
    price: "From £999",
    tagline: "For businesses wanting a completely bespoke digital experience.",
    features: [
      "Bespoke strategy and creative direction", "Custom page structure", "Cinematic opening experience",
      "Advanced interactions and animations", "Selective custom 3D", "Premium page transitions",
      "Advanced enquiry, quotation or booking-request forms", "Bespoke lead-generation journeys",
      "Expanded SEO foundations", "Priority communication", "Final quality checks and handover", "Personal launch support",
    ],
  },
];

const PACKAGE_OPTIONS = {
  Launch: "CLOSER. LAUNCH — £399",
  Growth: "CLOSER. GROWTH — £699",
  "Black Label": "CLOSER. BLACK LABEL — From £999",
};

const PackageCard = ({ pkg, i, onChoose }) => (
  <Reveal i={i} className="h-full">
    <div
      data-testid={`package-card-${pkg.name.toLowerCase().replace(" ", "-")}`}
      className={`group relative flex min-h-[690px] w-full flex-col rounded-[1.75rem] px-7 py-9 sm:min-h-[720px] sm:px-8 sm:py-10 transition-[transform,background-color,border-color,box-shadow] duration-500 hover:-translate-y-2 ${
        pkg.popular
          ? "glass border-white/30 shadow-[0_0_60px_rgba(255,255,255,0.07)] bg-white/[0.06]"
          : "glass hover:border-white/25 hover:bg-white/[0.06]"
      }`}
    >
      {pkg.popular && (
        <span
          data-testid="package-most-popular-badge"
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-1.5 font-display text-[10px] font-bold tracking-[0.25em] text-black uppercase"
        >
          Most Selected
        </span>
      )}
      <h3 className="font-display text-sm font-semibold tracking-[0.3em] text-white/70 uppercase">{pkg.displayName}</h3>
      <p data-testid={`package-price-${i}`} className="font-display mt-4 text-5xl font-bold tracking-tighter chrome-text">
        {pkg.price}
      </p>
      <p className="mt-5 min-h-[66px] text-sm font-light leading-relaxed text-white/50">{pkg.tagline}</p>
      <div className="my-8 h-px bg-gradient-to-r from-white/25 to-transparent" />
      <ul className="flex-1 space-y-3">
        {pkg.features.map((f, fi) => (
          <li key={fi} className={`flex items-start gap-2.5 text-[13px] leading-snug ${f.startsWith("Everything") ? "font-semibold text-white/85" : "font-light text-white/55"}`}>
            <Check size={13} className="mt-0.5 shrink-0 text-white/60" strokeWidth={2.5} />
            {f}
          </li>
        ))}
      </ul>
      <button
        data-testid={`package-cta-${pkg.name.toLowerCase().replace(" ", "-")}`}
        onClick={() => { onChoose(PACKAGE_OPTIONS[pkg.name]); scrollToSection("contact"); }}
        className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-semibold transition-colors duration-300 ${
          pkg.popular
            ? "bg-white text-black hover:bg-white/85"
            : "border border-white/25 text-white hover:bg-white hover:text-black"
        }`}
      >
        {pkg.name === "Black Label" ? "Discuss Black Label" : `Select ${pkg.name}`} <ArrowRight size={15} />
      </button>
    </div>
  </Reveal>
);

const Packages = ({ onChoose }) => {
  const [compare, setCompare] = useState(false);
  const [activeIdx, setActiveIdx] = useState(1);
  const active = PACKAGES[activeIdx];

  return (
  <section id="packages" data-testid="packages-section" className="relative z-10 py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-6 sm:px-10">
      <SectionHeading
        kicker="Packages"
        title="Choose Your Level of Engagement."
        sub="Three clear levels. One exacting standard. Select the starting point that fits your ambition."
        testId="packages-heading"
        align="center"
      />
      <Reveal i={3}>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <button
            data-testid="compare-packages-toggle"
            onClick={() => setCompare((c) => !c)}
            className="group inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.04] px-6 py-3 text-[13px] font-medium text-white/70 backdrop-blur-md transition-colors duration-300 hover:border-white/40 hover:text-white"
          >
            {compare ? <LayoutGrid size={15} /> : <Rows3 size={15} />}
            {compare ? "Back to Packages" : "Compare Packages"}
          </button>
          <div data-testid="package-tabs" className="inline-flex flex-wrap justify-center gap-1 rounded-full border border-white/15 bg-white/[0.03] p-1.5 backdrop-blur-md">
            {PACKAGES.map((p, i) => (
              <button
                key={p.name}
                data-testid={`package-tab-${p.name.toLowerCase().replace(" ", "-")}`}
                onClick={() => { setActiveIdx(i); setCompare(false); }}
                className={`rounded-full px-5 py-2 text-[12px] font-semibold tracking-wide transition-colors duration-300 ${
                  !compare && activeIdx === i
                    ? "bg-white text-black"
                    : "text-white/55 hover:text-white"
                }`}
              >
                {p.displayName}
              </button>
            ))}
          </div>
        </div>
      </Reveal>
      <AnimatePresence mode="wait">
        {compare ? (
          <CompareTable key="table" onChoose={onChoose} />
        ) : (
          <motion.div
            key={active.name}
            initial={{ opacity: 0, y: 30, scale: 0.97, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -20, scale: 0.98, filter: "blur(8px)" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-14 w-full max-w-[370px]"
          >
            <PackageCard pkg={active} i={0} onChoose={onChoose} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </section>
  );
};

export default Packages;
